import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Box,
  Grid,
  IconButton,
  Link,
  Modal,
  Select,
  Typography,
  TextField,
  Stack,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill-new';
import moment from 'moment';
import { POST_SERVER, CODE_SERVER } from '../../Config';
import axios from 'axios';
axios.defaults.withCredentials = true;

const DeleteModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  maxHeight: '85vh',
  bgcolor: 'rgb(253,237,237)',
  border: '2px solid #000',
  boxShadow: 24,
  p: 2,
  borderRadius: '12px',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const CustomTitleTypography = styled(Typography)(({ theme }) => ({
  color: '#424242',
  fontSize: '14px',
  fontWeight: 600,
  minWidth: '120px',
  textAlign: 'left',
}));

const CustomSubtitleTypography = styled(Typography)(({ theme }) => ({
  color: '#9E9E9E',
  fontSize: '11px',
  mb: 0.25,
}));

const CustomDetailTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: '#424242',
  fontSize: '15px',
}));

const CustomSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid #e8e8e8',
  mb: 2,
}));

const CustomFormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  py: 1,
  marginBottom: 3,
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const CustomButton = styled(Button)(({ theme }) => ({
  minWidth: '40px',
  height: '40px',
  marginLeft: '8px',
  textTransform: 'none',
  color: 'white',
  backgroundColor: '#6c757d',
  borderRadius: '6px',
  '&:hover': { backgroundColor: '#545b62' },
}));

const FileUploadContainer = styled(Box)(({ theme }) => ({
  border: '2px dashed #d1d5db',
  borderRadius: '10px',
  padding: '20px',
  backgroundColor: '#f8fafc',
  minHeight: '280px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  gap: '12px',
  overflowY: 'auto',
}));

const FileItem = styled(Box)(({ theme }) => ({
  padding: '4px 4px',
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  boxSizing: 'border-box',
}));

const FileNameText = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  wordBreak: 'break-all',
  lineHeight: '1.3',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: '#1f2937',
  mb: 2,
  pb: 1.5,
  borderBottom: '2px solid #e5e7eb',
}));

const viewModeModules = { toolbar: false };

const isMetadataFile = (name) => {
  const lower = name.toLowerCase();
  return lower.includes('metadata') && (lower.includes('.xlsx') || lower.includes('.xls'));
};

const formatUploadTime = (dateStr) => {
  if (!dateStr) return '';
  return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
};

const ProjectSubSubComponent = (props) => {
  const [domainList, setDomainList] = useState([]);
  const [dataTargetList, setDataTargetList] = useState([]);
  const [dataTypeList, setDataTypeList] = useState([]);
  const [accessLevelList, setAccessLevelList] = useState([]);
  const [postData, setPostData] = useState();
  const [modifyStatus, setModifyStatus] = useState(false);
  const [deleteFileList, setDeleteFileList] = useState([]);
  const [addFileList, setAddFileList] = useState([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [uploadProgresses, setUploadProgresses] = useState([]);
  const [tempFileNames, setTempFileNames] = useState([]);
  const [pendingDataType, setPendingDataType] = useState(null); // 추가

  const user = useSelector((state) => state.user.userData);

  const { handleSubmit, register, setValue, watch, control, reset } = useForm({
    defaultValues: {
      collectionPeriod: { startDate: null, endDate: null },
      domain: '',
      dataTarget: '',
      dataType: '',
      sampleCount: '',
      researchDirector: '',
      accessLevel: '',
      collectionMethod: '',
    },
  });

  const watchedDomain = watch('domain');

  // dataTypeList 로드 완료 후 pendingDataType 세팅
  useEffect(() => {
    if (!watchedDomain) {
      setDataTypeList([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/${watchedDomain}`)
      .then((res) => {
        setDataTypeList(res.data);
        if (pendingDataType !== null) {
          setValue('dataType', pendingDataType);
          setPendingDataType(null);
        }
      })
      .catch(() => setDataTypeList([]));
  }, [watchedDomain]);

  const handleClose = () => setOpenConfirmModal(false);

  const downloadAttachedFile = async (postId, name, idx) => {
    window.open(`${process.env.REACT_APP_SERVER}${POST_SERVER}/file/${postId}/${idx}`, '_blank', 'noopener,noreferrer');
  };

  const attachFileOnChange = (postData, idx, category) => {
    if (category === 'delete') {
      setDeleteFileList((prev) =>
        prev.includes(idx) ? prev.filter((deleteIdx) => deleteIdx !== idx) : [...prev, idx]
      );
    }
  };

  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const newProgresses = files.map((file) => ({
      id: `${file.name}_${Date.now()}_${Math.random()}`,
      fileName: file.name,
      progress: 0,
      status: 'pending',
    }));
    setUploadProgresses((prev) => [...prev, ...newProgresses]);

    const filesWithId = files.map((file, i) => ({
      id: newProgresses[i].id,
      name: file.name,
      file: file,
    }));
    setAddFileList((prev) => [...prev, ...filesWithId]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progressId = newProgresses[i].id;
      const formData = new FormData();
      formData.append('files', file);
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgresses((prev) =>
            prev.map((p) => (p.id === progressId ? { ...p, progress: percent, status: 'uploading' } : p))
          );
        }
      });

      xhr.addEventListener('load', () => {
        const status = xhr.status === 200 ? 'done' : 'error';
        setUploadProgresses((prev) => prev.map((p) => (p.id === progressId ? { ...p, status } : p)));
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.ok && Array.isArray(response.files) && response.files.length > 0) {
              const serverName = response.files[0];
              setTempFileNames((prev) => [...prev, { id: progressId, tempName: serverName, originalName: file.name }]);
            }
          } catch (err) {
            console.error('Response parse error:', err);
          }
        }
      });

      xhr.open('POST', `${process.env.REACT_APP_SERVER}${POST_SERVER}/upload-temp`);
      xhr.send(formData);
    }
    event.target.value = '';
  };

  const removeTempFile = async (progressId) => {
    const target = tempFileNames.find((f) => f.id === progressId);
    if (target) {
      try {
        await axios.delete(`${process.env.REACT_APP_SERVER}${POST_SERVER}/temp-delete/${target.tempName}`);
      } catch (err) {
        console.error('Temp delete failed:', err);
      }
      setTempFileNames((prev) => prev.filter((f) => f.id !== progressId));
    }
    setUploadProgresses((prev) => prev.filter((p) => p.id !== progressId));
    setAddFileList((prev) => prev.filter((f) => f.id !== progressId));
  };

  const deletePost = () => {
    axios.delete(`${process.env.REACT_APP_SERVER}${POST_SERVER}/${props.rowId}`).then((res) => {
      if (res.data.ok) {
        handleClose();
        props.rowClear();
        props.reloadList();
      }
    });
  };

  const onSubmit = async (data) => {
    if (uploadProgresses.some((p) => p.status === 'uploading')) {
      enqueueSnackbar('업로드 완료 후 저장하세요.', { variant: 'warning' });
      return;
    }

    const existingMetadata = postData?.fileName?.some(
      (name, idx) => !deleteFileList.includes(idx) && isMetadataFile(name)
    );
    const newMetadata = addFileList.some((f) => isMetadataFile(f.name));
    if (!existingMetadata && !newMetadata) {
      enqueueSnackbar('Metadata 엑셀 파일이 없으면 저장할 수 없습니다.', { variant: 'error' });
      return;
    }

    const formData = new FormData();
    formData.append('modifyUser', user._id);
    formData.append('modifyDate', new Date().toISOString());
    formData.append('collectionPeriod', JSON.stringify(data.collectionPeriod));
    formData.append('domain', data.domain);
    formData.append('dataTarget', data.dataTarget);
    formData.append('dataType', data.dataType);
    formData.append('sampleCount', data.sampleCount);
    formData.append('researchDirector', data.researchDirector);
    formData.append('accessLevel', data.accessLevel);
    formData.append('collectionMethod', data.collectionMethod);

    deleteFileList.forEach((deleteIdx) => formData.append('deleteFileIndices', deleteIdx));
    tempFileNames.forEach(({ tempName, originalName }) => {
      formData.append('tempFiles', tempName);
      formData.append('originalFileNames', originalName);
    });

    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER}${POST_SERVER}/${props.rowId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.ok) {
        setModifyStatus(false);
        setDeleteFileList([]);
        setAddFileList([]);
        setUploadProgresses([]);
        setTempFileNames([]);
        const postRes = await axios.get(`${process.env.REACT_APP_SERVER}${POST_SERVER}/${props.rowId}`);
        setPostData(postRes.data);
        enqueueSnackbar('저장 완료!', { variant: 'success' });
        props.reloadList();
      }
    } catch (error) {
      enqueueSnackbar('저장 실패', { variant: 'error' });
    }
  };

  // dataType은 pending으로 보관, domain 세팅 후 dataTypeList 로드 완료되면 자동 세팅
  useEffect(() => {
    if (props.rowId) {
      Promise.all([
        axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/10`),
        axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/20`),
        axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/40`),
        axios.get(`${process.env.REACT_APP_SERVER}${POST_SERVER}/${props.rowId}`),
      ]).then(([domainRes, targetRes, levelRes, postRes]) => {
        setDomainList(domainRes.data);
        setDataTargetList(targetRes.data);
        setAccessLevelList(levelRes.data);
        setPostData(postRes.data);

        const data = postRes.data;
        setValue('collectionPeriod', data.collectionPeriod);
        setValue('dataTarget', data.dataTarget || '');
        setValue('sampleCount', data.sampleCount || '');
        setValue('researchDirector', data.researchDirector || '');
        setValue('accessLevel', data.accessLevel || '');
        setValue('collectionMethod', data.collectionMethod || '');

        // dataType은 dataTypeList 로드 후 세팅하기 위해 pending 보관
        if (data.dataType) {
          setPendingDataType(data.dataType);
        }

        // domain은 마지막 → watchedDomain 트리거 → dataTypeList 조회 시작
        setValue('domain', data.domain || '');
      });
    }
  }, [props.rowId, setValue]);

  useEffect(() => {
    if (postData) setModifyStatus(false);
  }, [postData]);

  return (
    <div>
      {props.rowId && postData && (
        <Box sx={{ width: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              p: 2,
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CustomButton onClick={() => props.rowClear()}>
                <ArrowForwardIcon />
              </CustomButton>
              <Typography variant="h6" sx={{ fontWeight: 700, ml: 1.5 }}>
                데이터 상세 정보
              </Typography>
            </Box>
          </Box>

          {/* Main Content */}
          <Grid container>
            <Grid size={{ xs: 12 }}>
              {/* Basic Info */}
              <CustomSection>
                <SectionTitle>기본 정보</SectionTitle>
                <CustomFormRow>
                  <CustomTitleTypography>연구기관</CustomTitleTypography>
                  <Box sx={{ flex: 1 }}>
                    <CustomDetailTypography>{postData.groupName}</CustomDetailTypography>
                  </Box>
                </CustomFormRow>
                <CustomFormRow>
                  <CustomTitleTypography>작성자</CustomTitleTypography>
                  <Box sx={{ flex: 1 }}>
                    <CustomDetailTypography>{postData.publisherName}</CustomDetailTypography>
                  </Box>
                </CustomFormRow>
                <CustomFormRow>
                  <CustomTitleTypography>생성일</CustomTitleTypography>
                  <Box sx={{ flex: 1 }}>
                    <CustomDetailTypography>
                      {moment(postData.createDate).format('YYYY.MM.DD HH:mm')}
                    </CustomDetailTypography>
                  </Box>
                </CustomFormRow>
                <CustomFormRow>
                  <CustomTitleTypography>데이터 수집기간</CustomTitleTypography>
                  <Box sx={{ flex: 1 }}>
                    <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                      <Grid cols={6}>
                        <Controller
                          control={control}
                          name="collectionPeriod.startDate"
                          render={({ field: { value, onChange } }) => (
                            <TextField
                              fullWidth
                              size="small"
                              disabled={!modifyStatus}
                              label="시작일"
                              type="date"
                              value={value ? moment(value).format('YYYY-MM-DD') : ''}
                              onChange={(e) => {
                                const date = e.target.value ? moment(e.target.value).toDate() : null;
                                onChange(date);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiInputBase-root': { height: 40 } }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid cols={6}>
                        <Controller
                          control={control}
                          name="collectionPeriod.endDate"
                          render={({ field: { value, onChange } }) => (
                            <TextField
                              fullWidth
                              size="small"
                              disabled={!modifyStatus}
                              label="종료일"
                              type="date"
                              value={value ? moment(value).format('YYYY-MM-DD') : ''}
                              onChange={(e) => {
                                const date = e.target.value ? moment(e.target.value).toDate() : null;
                                onChange(date);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiInputBase-root': { height: 40 } }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </CustomFormRow>
              </CustomSection>

              {/* Data Info */}
              <CustomSection>
                <SectionTitle>데이터 정보</SectionTitle>

                <CustomFormRow>
                  <Box>
                    <CustomTitleTypography>Domain</CustomTitleTypography>
                    <CustomSubtitleTypography>(도메인)</CustomSubtitleTypography>
                  </Box>
                  <Controller
                    control={control}
                    name="domain"
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        size="small"
                        disabled={!modifyStatus}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setValue('dataType', ''); // 사용자가 바꿀 때 초기화
                          setPendingDataType(null); // pending도 초기화
                        }}
                        sx={{ minWidth: 200 }}
                      >
                        {domainList.map((v) => (
                          <MenuItem key={v._id} value={v.code}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </CustomFormRow>

                <CustomFormRow>
                  <Box>
                    <CustomTitleTypography>Data Target</CustomTitleTypography>
                    <CustomSubtitleTypography>(데이터 대상)</CustomSubtitleTypography>
                  </Box>
                  <Select
                    fullWidth
                    size="small"
                    disabled={!modifyStatus}
                    value={watch('dataTarget') || ''}
                    {...register('dataTarget')}
                    sx={{ minWidth: 200 }}
                  >
                    {dataTargetList.map((v) => (
                      <MenuItem key={v._id} value={v.code}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </CustomFormRow>

                <CustomFormRow>
                  <Box>
                    <CustomTitleTypography>Data Type</CustomTitleTypography>
                    <CustomSubtitleTypography>(데이터 유형)</CustomSubtitleTypography>
                  </Box>
                  <Controller
                    control={control}
                    name="dataType"
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        size="small"
                        disabled={!modifyStatus || !watchedDomain}
                        displayEmpty
                        sx={{ minWidth: 200 }}
                      >
                        {!watchedDomain && (
                          <MenuItem value="" disabled>
                            Domain을 먼저 선택하세요
                          </MenuItem>
                        )}
                        {dataTypeList.map((v) => (
                          <MenuItem key={v._id} value={v.code}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </CustomFormRow>

                <CustomFormRow>
                  <Box>
                    <CustomTitleTypography>Access Level</CustomTitleTypography>
                    <CustomSubtitleTypography>(접근 권한)</CustomSubtitleTypography>
                  </Box>
                  <Select
                    fullWidth
                    size="small"
                    disabled={!modifyStatus}
                    value={watch('accessLevel') || ''}
                    {...register('accessLevel')}
                    sx={{ minWidth: 200 }}
                  >
                    {accessLevelList.map((v) => (
                      <MenuItem key={v._id} value={v.code}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </CustomFormRow>

                <CustomFormRow>
                  <Box>
                    <CustomTitleTypography>Sample Count</CustomTitleTypography>
                    <CustomSubtitleTypography>(샘플 수)</CustomSubtitleTypography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="샘플 수 입력"
                    disabled={!modifyStatus}
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === '+' || e.key === 'e') {
                        e.preventDefault();
                      }
                    }}
                    {...register('sampleCount', {
                      min: { value: 0, message: '0 이상의 값을 입력하세요' },
                      onChange: (e) => {
                        if (Number(e.target.value) < 0) setValue('sampleCount', 0);
                      },
                    })}
                  />
                </CustomFormRow>

                <CustomFormRow>
                  <CustomTitleTypography>Research Director</CustomTitleTypography>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={!modifyStatus}
                    {...register('researchDirector')}
                    sx={{ minWidth: 200 }}
                  />
                </CustomFormRow>

                <CustomFormRow>
                  <CustomTitleTypography>수집 방법</CustomTitleTypography>
                  <ReactQuill
                    modules={viewModeModules}
                    style={{ width: '100%', height: '140px', borderRadius: '8px' }}
                    theme="snow"
                    readOnly={!modifyStatus}
                    value={watch('collectionMethod') || ''}
                    onChange={(v) => setValue('collectionMethod', v)}
                  />
                </CustomFormRow>
              </CustomSection>

              {/* File Upload */}
              <CustomSection>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionTitle>데이터 업로드</SectionTitle>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    sx={{ textTransform: 'none' }}
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_SERVER}${POST_SERVER}/metadata/template`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    Metadata Template
                  </Button>
                </Box>
                <FileUploadContainer sx={{ minHeight: '200px' }}>
                  <Stack spacing={2}>
                    {/* Existing Files */}
                    {postData.fileName?.length > 0 && (
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#6b7280', mb: 1, fontWeight: 600, fontSize: '13px', width: '120px' }}
                        >
                          📁 기존 파일 ({postData.fileName.length}개)
                        </Typography>
                        {postData.fileName.map((name, idx) => (
                          <FileItem key={`existing-${idx}`}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Link
                                component="button"
                                underline="hover"
                                onClick={() => downloadAttachedFile(postData._id, name, idx)}
                                sx={{
                                  flexGrow: 1,
                                  p: 0.5,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <AttachFileIcon sx={{ fontSize: 18, mr: 1.5, color: '#6b7280', mt: '2px' }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <FileNameText title={name}>{name}</FileNameText>
                                  {postData?.uploadedAt?.[idx] && (
                                    <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>
                                      {formatUploadTime(postData.uploadedAt[idx])}
                                    </Typography>
                                  )}
                                </Box>
                              </Link>
                              {modifyStatus && (
                                <IconButton
                                  size="small"
                                  onClick={() => attachFileOnChange(postData, idx, 'delete')}
                                  color={deleteFileList.includes(idx) ? 'error' : 'default'}
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          </FileItem>
                        ))}
                      </Box>
                    )}

                    {/* Temp Upload Files */}
                    {addFileList.length > 0 && (
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#3b82f6', mb: 1, fontWeight: 600, fontSize: '13px' }}
                        >
                          ⏳ 임시 업로드 ({addFileList.length}개)
                        </Typography>
                        {addFileList.map((file, idx) => {
                          const progress = uploadProgresses.find((p) => p.fileName === file.name);
                          return (
                            <FileItem key={`temp-${idx}`} sx={{ width: '100%' }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                  <AttachFileIcon sx={{ fontSize: 18, mr: 1.5, color: '#3b82f6' }} />
                                  <FileNameText title={file.name}>{file.name}</FileNameText>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => removeTempFile(file.id)}
                                  color="error"
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                              {progress && (
                                <ProgressContainer sx={{ width: '100%' }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress.progress}
                                    sx={{ flexGrow: 1, height: 6, borderRadius: '3px' }}
                                    color={
                                      progress.status === 'done'
                                        ? 'success'
                                        : progress.status === 'error'
                                        ? 'error'
                                        : 'primary'
                                    }
                                  />
                                  <Box sx={{ minWidth: 32, fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>
                                    {progress.status === 'done' && '완료'}
                                    {progress.status === 'error' && '오류'}
                                    {progress.status === 'uploading' && `${progress.progress}%`}
                                  </Box>
                                </ProgressContainer>
                              )}
                            </FileItem>
                          );
                        })}
                      </Box>
                    )}

                    {/* Add File Button */}
                    {modifyStatus && (
                      <Box
                        sx={{
                          pt: 2,
                          borderTop: '1px dashed #d1d5db',
                          width: '100%',
                          textAlign: 'center',
                          mt: 'auto',
                        }}
                      >
                        <IconButton
                          component="label"
                          size="large"
                          sx={{
                            color: '#6b7280',
                            border: '2px dashed #d1d5db',
                            borderRadius: '50%',
                            width: 64,
                            height: 64,
                            '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6', color: '#3b82f6' },
                          }}
                        >
                          <input type="file" multiple hidden accept="*/*" onChange={handleFileInputChange} />
                          <AddCircleOutlineIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                        <Typography variant="body2" sx={{ mt: 1, color: '#6b7280', fontWeight: 500, fontSize: '12px' }}>
                          파일 추가 (다중 선택 가능)
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </FileUploadContainer>
              </CustomSection>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.5 }}>
            <CustomButton onClick={() => setOpenConfirmModal(true)}>
              <DeleteIcon />
              <Typography sx={{ ml: 1, fontSize: '13px', fontWeight: 500 }}>삭제</Typography>
            </CustomButton>
            <CustomButton
              onClick={async () => {
                if (tempFileNames.length > 0) {
                  for (const { tempName } of tempFileNames) {
                    try {
                      await axios.delete(`${process.env.REACT_APP_SERVER}${POST_SERVER}/temp-delete/${tempName}`);
                    } catch (err) {
                      console.error('Temp delete failed:', tempName, err);
                    }
                  }
                  setTempFileNames([]);
                }
                setAddFileList([]);
                setUploadProgresses([]);
                setDeleteFileList([]);
                if (modifyStatus) reset();
                setModifyStatus(!modifyStatus);
              }}
            >
              <EditIcon />
              <Typography sx={{ ml: 1, fontSize: '13px', fontWeight: 500 }}>
                {modifyStatus ? '취소' : '수정'}
              </Typography>
            </CustomButton>
            {modifyStatus && (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckIcon />}
                onClick={handleSubmit(onSubmit)}
                disabled={uploadProgresses.some((p) => p.status === 'uploading')}
                sx={{
                  ml: '8px',
                  height: '40px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
                }}
              >
                저장하기
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Delete Confirm Modal */}
      <Modal open={openConfirmModal} onClose={handleClose}>
        <Box sx={DeleteModalStyle}>
          <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
            데이터 삭제 확인
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.5 }}>
            이 작업은 <strong>복구할 수 없습니다.</strong>
            <br />
            연구자 데이터를 정말 삭제하시겠습니까?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
            <Button variant="outlined" onClick={handleClose}>
              취소
            </Button>
            <Button variant="contained" color="error" onClick={deletePost}>
              삭제하기
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ProjectSubSubComponent;
